/**
 * As cores do app, e a regra de derivar tons a partir de uma cor-base.
 *
 * Guarda-se sempre o NOME-BASE do Vuetify — `teal`, `indigo` —, nunca um hexadecimal: é
 * o que permite pedir o tom certo para cada papel na hora de desenhar. A faixa do Kanban
 * é o exemplo completo: `lighten-5` no fundo, `darken-1` na barrinha e `darken-3` no
 * título, tudo derivado de uma cor só.
 *
 * Vive em utils, e não junto do StateColorizer, porque o design também escolhe cor — o
 * domínio de um atributo de seleção — e a paleta não pode morar dentro da pasta de
 * instância. O próprio types/Workflow.ts importava de lá, um tipo dependendo de um
 * componente.
 */

/** Cores da paleta que o Material NÃO define com tons `accent-*`. Detalhe do `shade`. */
const WITHOUT_ACCENT = ['brown', 'grey', 'blue-grey']

/**
 * A cor da paleta num tom: `teal` + `accent-4` = `teal-accent-4`, que é o nome que o
 * Vuetify 3+ aceita no `color` e nas classes `bg-*`/`text-*`.
 *
 * Tom `accent-*` que a cor não tem cai na cor-base. Era o que o Vuetify 2 fazia sem
 * querer — a classe `accent-4` não existia para `brown`, e só a `brown` pegava —, e sem
 * esta regra `brown-accent-4` seria um nome inválido, e o card, sem fundo.
 */
export function shade(color: string, tone: string): string {
    return tone.startsWith('accent') && WITHOUT_ACCENT.includes(color) ? color : `${color}-${tone}`
}

/**
 * O cinza é RESERVADO, e por isso não está na paleta nem na ordem de atribuição.
 *
 * Ele quer dizer uma coisa só no app: "sem valor" — a raia e o bloco de quem tem a propriedade
 * vazia. Se uma opção de domínio ou uma situação pudesse ser cinza, o quadro teria duas coisas
 * diferentes pintadas igual, e a cor deixaria de informar.
 *
 * Continua sendo cor de DESENHO válida: `shade` a aceita como qualquer outra, e é dela que
 * saem os tons da raia do grupo vazio.
 */
export const NO_VALUE_COLOR = 'grey'

/**
 * Todas as cores do Material que têm a escala inteira de tons, e por isso servem a
 * qualquer papel.
 *
 * `black` e `white` ficam de fora de propósito: não têm `lighten-*` nem `darken-*`, e
 * toda tela que pintasse com um tom ficaria sem fundo. Foi o defeito que tirou o `black`
 * da paleta das situações.
 *
 * Esta é a paleta que o usuário escolhe, no domínio. A das situações é um subconjunto
 * dela, porque lá vermelho e verde são reservados para inicial e final — e o cinza está fora
 * das duas, pelo motivo do `NO_VALUE_COLOR`.
 */
export const PALETTE = [
    'red', 'pink', 'purple', 'deep-purple', 'indigo', 'blue', 'light-blue', 'cyan',
    'teal', 'green', 'light-green', 'lime', 'yellow', 'amber', 'orange', 'deep-orange',
    'brown', 'blue-grey'
]

/**
 * A ordem em que as cores são DADAS, uma por opção nova: a mesma paleta, embaralhada para
 * que duas vizinhas nunca sejam parecidas.
 *
 * A ordem da PALETTE é a do espectro, e dar cores nela punha vermelho, rosa e roxo em
 * sequência — as três primeiras opções de um domínio mal se distinguiam. Aqui cada cor
 * está longe da anterior: primárias primeiro, depois as intermediárias, e o tom apagado
 * (`blue-grey`) longe das famílias de azul.
 *
 * O menu de amostras NÃO usa esta ordem: lá se procura uma cor, e famílias juntas são o
 * que torna a procura rápida.
 *
 * Serve a três coisas pelo mesmo caminho: a opção nova de um domínio, a situação sem cor
 * escolhida (com a ordem própria dela) e as raias de um relacionamento, que não têm cor no
 * dado e a recebem da posição.
 */
export const ASSIGNMENT_ORDER = [
    'blue', 'red', 'yellow', 'purple', 'orange', 'green', 'light-blue', 'deep-orange',
    'lime', 'blue-grey', 'pink', 'teal', 'amber', 'indigo', 'brown', 'cyan',
    'deep-purple', 'light-green'
]

/**
 * A cor da enésima coisa a colorir; passado o fim da ordem, recomeça.
 *
 * A volta ao começo é o que uma paleta fechada permite — repetir cor é melhor que ficar sem
 * nenhuma —, e mora aqui para ser a MESMA regra de quem dá cor pelo id e de quem dá a próxima
 * livre. Privada: quem vem de fora pede uma das duas — `instanceColor` ou `nextColor` —, e
 * não o índice cru.
 */
function colorAt(index: number, order: string[] = ASSIGNMENT_ORDER): string {
    return order[index % order.length]
}

/**
 * A cor de uma instância, pelo id dela.
 *
 * É a conta mais simples que resolve — `id % quantidade de cores` —, e ela não se escreve aqui:
 * o `colorAt` já É essa função, sobre a mesma `ASSIGNMENT_ORDER` que dá cor às opções
 * automáticas de um domínio. Ids vizinhos caem em cores distantes porque aquela ordem foi
 * embaralhada para isso; passadas as 18, repete.
 *
 * Derivada, e não gravada: a mesma instância tem a mesma cor em toda tela, em toda sessão e sem
 * nada no banco. Se um dia se quiser escolher a cor de um item, esta vira o padrão e o valor
 * gravado a sobrepõe.
 *
 * O cinza continua fora, porque está fora da ordem: nenhuma instância é cinza, e o cinza segue
 * querendo dizer uma coisa só — "Não definido".
 */
export function instanceColor(id: number): string {
    return colorAt(id)
}

/**
 * A cor de uma pessoa, pelo id da conta dela (`user`).
 *
 * Mesma ideia do `instanceColor` e mesma paleta. Era pelo e-mail, de quando ele era a identidade;
 * passou ao id quando o e-mail deixou de ir às telas de uso (decisão do usuário: quem edita não
 * precisa saber o endereço de ninguém) — e o id é o que toda tela tem da pessoa, das de uso às de
 * administração, então a cor é a mesma em todas.
 *
 * Derivada e não gravada, pelo mesmo motivo de lá: é a mesma cor em toda tela e em toda sessão,
 * sem nada no banco.
 */
export function personColor(user: number): string {
    return colorAt(user)
}

/**
 * Os três papéis de uma cor numa superfície: claro no fundo, médio no acento, escuro no texto.
 *
 * Moram aqui, e não junto do Kanban, porque não são do Kanban: a raia os usa, o menu de raias
 * usa a miniatura deles, e a janela da ficha usa o acento. Um cabeçalho importando o layout do
 * quadro seria a inversão de camada de sempre.
 *
 * **Sem cor devolve vazio**, e não `undefined-lighten-5`: uma classe inexistente deixaria a
 * superfície sem fundo sem ninguém saber por quê. Vazio é o que faz o CSS neutro valer.
 */
export function surfaceClass(color?: string): string {
    return color ? `bg-${shade(color, 'lighten-5')}` : ''
}

/** O tom do acento, como COR — é o que o checkbox do menu de raias recebe. */
export function accentColor(color?: string): string {
    return color ? shade(color, 'darken-1') : ''
}

/** O mesmo tom, como CLASSE — é o que a barra de acento recebe. */
export function accentClass(color?: string): string {
    const accent = accentColor(color)
    return accent ? `bg-${accent}` : ''
}

/**
 * O tom da TINTA: texto sobre fundo claro.
 *
 * `darken-3` e não a cor-base porque metade da paleta não se lê em cima de branco: amarelo,
 * lima e âmbar somem. Privada porque o único papel que sobrou para ela é o texto da raia — a
 * marca de um item virou régua, que é mancha e usa o tom do acento.
 */
function titleColor(color?: string): string {
    return color ? shade(color, 'darken-3') : ''
}

/** O mesmo tom, como CLASSE — é o que o texto sobre a superfície clara recebe. */
export function titleClass(color?: string): string {
    const ink = titleColor(color)
    return ink ? `text-${ink}` : ''
}

/**
 * O valor tingido: o fundo claro e a tinta da mesma cor, como CLASSES. Um dono para o chip de uma opção
 * de domínio (`ColoredOptionChip`), a bolinha de quem não tem foto (`personAvatar`) e a referência do
 * markdown — o par estava escrito nos dois primeiros.
 *
 * `lighten-4` e não o `lighten-5` da faixa do Kanban: lá é uma superfície grande, aqui é o fundo de um
 * nome curto sobre papel branco, e o tom mais pálido não se distingue dele. Sem cor, nenhuma: sai neutro
 * em vez de virar `undefined-lighten-4`, uma classe que não existe.
 */
export function tintClasses(color?: string): string[] {
    return color ? [`bg-${shade(color, 'lighten-4')}`, titleClass(color)] : []
}

/** A próxima cor ainda não usada, na ordem dada; esgotadas, recomeça pelo `colorAt`. */
export function nextColor(used: string[], order: string[] = ASSIGNMENT_ORDER): string {
    return order.find(color => !used.includes(color)) || colorAt(used.length, order)
}
